/**
 * Onboarding Pointers
 *
 * Renders three tooltip pointers on the invoice detail page after onboarding:
 * 1. Export button → "Exportuj doklad"
 * 2. QR Payment button → "Zaplať QR kódem"
 * 3. Navbar (documents) → "Vytěžit další doklad"
 *
 * Desktop: Floating tooltips with arrows pointing to target elements.
 * Mobile: Sticky bottom stack of compact cards (no arrows/positioning needed).
 *
 * Each tooltip dismisses independently and permanently (via localStorage).
 *
 * Feature: Onboarding
 */
import { useCallback, useState } from 'react';
import { Button, Icon, useMediaQuery } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { ONBOARDING_HIGHLIGHT } from '../constants/debug';
import { OnboardingTooltip } from './OnboardingTooltip';

/** localStorage key prefix — shared with OnboardingTooltip */
const STORAGE_PREFIX = 'onboarding_tooltip_dismissed_';

/** Desktop pointers render at/above the xl breakpoint (1280px); mobile cards below it. */
const DESKTOP_QUERY = '(min-width: 1280px)';

/** Reactively tracks whether the viewport is at/above the xl breakpoint. */
function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_QUERY);
}

export interface OnboardingPointersProps {
  /** Whether the tooltip system should be active */
  active: boolean;
  /** Translated label for the export action (used to build anchor selector) */
  exportLabel: string;
  /** Translated label for the QR payment action (used to build anchor selector) */
  qrPaymentLabel: string;
  /** User ID to scope localStorage keys (prevents cross-user leakage) */
  userId?: string;
}

interface TooltipItem {
  key: string;
  title: string;
  text: string;
  /** CSS selector for the element to scroll to and highlight on mobile tap */
  mobileSelector: string;
}

/**
 * Renders onboarding tooltip pointers for the invoice detail page.
 * Desktop: floating tooltips with arrows. Mobile: sticky bottom cards.
 */
export function OnboardingPointers({
  active,
  exportLabel,
  qrPaymentLabel,
  userId
}: OnboardingPointersProps) {
  const t = useFeatureTranslation('onboarding');
  const isDesktop = useIsDesktop();

  if (!active) return null;

  const items: TooltipItem[] = [
    {
      key: 'export',
      title: t('tooltips.export.title'),
      text: t('tooltips.export.text'),
      mobileSelector: `[data-onboarding-mobile="${exportLabel}"]`
    },
    {
      key: 'qr-payment',
      title: t('tooltips.qrPayment.title'),
      text: t('tooltips.qrPayment.text'),
      mobileSelector: `[data-onboarding-mobile="${qrPaymentLabel}"]`
    },
    {
      key: 'next-document',
      title: t('tooltips.nextDocument.title'),
      text: t('tooltips.nextDocument.text'),
      mobileSelector: '[data-onboarding-mobile="burger"]'
    }
  ];

  // Desktop tooltips portal to document.body, so CSS-class gating on a wrapper
  // no longer hides them — gate desktop vs. mobile in JS at the xl breakpoint.
  if (isDesktop) {
    return (
      <>
        {/* Export button — far right; tooltip goes left, appears highest */}
        <OnboardingTooltip
          storageKey='export'
          anchorSelector={`button[data-desktop-action="${exportLabel}"]`}
          title={items[0].title}
          text={items[0].text}
          active={active}
          slotX='left'
          verticalOffset={0}
          userId={userId}
        />
        {/* QR Payment button — right of export; tooltip goes left, staggered down */}
        <OnboardingTooltip
          storageKey='qr-payment'
          anchorSelector={`button[data-desktop-action="${qrPaymentLabel}"]`}
          title={items[1].title}
          text={items[1].text}
          active={active}
          slotX='left'
          verticalOffset={80}
          userId={userId}
        />
        {/* Documents nav link — far left; tooltip centered, staggered between export (0) and qr-payment (80) */}
        <OnboardingTooltip
          storageKey='next-document'
          anchorSelector='[data-onboarding="next-document"]'
          title={items[2].title}
          text={items[2].text}
          active={active}
          slotX='right'
          verticalOffset={60}
          userId={userId}
        />
      </>
    );
  }

  // Mobile (< xl): sticky bottom stack
  return <MobilePointers items={items} userId={userId} />;
}

const HIGHLIGHT_CLASS: Record<typeof ONBOARDING_HIGHLIGHT, string> = {
  ring: 'onboarding-flash-ring',
  bounce: 'onboarding-flash-bounce',
  glow: 'onboarding-flash-glow',
  default: 'validation-flash'
};

/** Scrolls to the element matching selector and applies the configured highlight. */
function useScrollAndHighlight() {
  return useCallback((selector: string) => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const cls = HIGHLIGHT_CLASS[ONBOARDING_HIGHLIGHT];
    // Remove class first in case it's already animating, then re-add on next frame
    el.classList.remove(cls);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add(cls);
        el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
      });
    });
  }, []);
}

/** Mobile-only sticky bottom tooltip cards */
function MobilePointers({ items, userId }: { items: TooltipItem[]; userId?: string }) {
  const scrollAndHighlight = useScrollAndHighlight();

  const keyFor = (key: string) =>
    userId ? `${STORAGE_PREFIX}${key}_${userId}` : `${STORAGE_PREFIX}${key}`;

  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => {
    const set = new Set<string>();
    for (const item of items) {
      if (localStorage.getItem(keyFor(item.key)) === 'true') {
        set.add(item.key);
      }
    }
    return set;
  });

  const handleDismiss = (key: string) => {
    localStorage.setItem(keyFor(key), 'true');
    setDismissedKeys(prev => new Set([...prev, key]));
  };

  const visibleItems = items.filter(item => !dismissedKeys.has(item.key));

  if (visibleItems.length === 0) return null;

  return (
    <div className='fixed bottom-0 inset-x-0 z-50 flex flex-col gap-xs p-sm pb-md'>
      {visibleItems.map(item => (
        <div
          key={item.key}
          role='button'
          tabIndex={0}
          onClick={() => scrollAndHighlight(item.mobileSelector)}
          onKeyDown={e => e.key === 'Enter' && scrollAndHighlight(item.mobileSelector)}
          className='flex items-start gap-sm rounded-lg bg-black p-sm shadow-xl cursor-pointer'
        >
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-semibold text-secondary-base'>{item.title}</p>
            <p className='text-xs text-white/80'>{item.text}</p>
          </div>
          <Button
            variant='ghost'
            type='button'
            onClick={e => {
              e.stopPropagation();
              handleDismiss(item.key);
            }}
            className='shrink-0 text-white/60 hover:text-white transition-colors'
            aria-label='Close'
          >
            <Icon name='xmark' size='sm' />
          </Button>
        </div>
      ))}
    </div>
  );
}
